import axios from 'axios'
import CryptoJS from 'crypto-js'

// Base URL for the secure public API
const BASE_URL = process.env.VUE_APP_API_PUBLIC_SECURE_URL

// Compute HMAC signature headers aligned with server middleware
function buildHmacHeaders ({ method, pathWithQuery, body = '' }, { secretKey }) {
  const normalizedMethod = method.toUpperCase()
  // Server expects empty string for GET, and exact JSON string for others
  const payload = normalizedMethod === 'GET'
    ? ''
    : (typeof body === 'string' ? body : JSON.stringify(body || ''))
  const timestamp = Date.now().toString() // unix ms as string
  const nonce = CryptoJS.lib.WordArray.random(8).toString()
  const signingString = [normalizedMethod, payload, timestamp, nonce].join('\n')
  const signature = CryptoJS.HmacSHA256(signingString, secretKey).toString(CryptoJS.enc.Hex)

  return {
    'x-timestamp': timestamp,
    'x-nonce': nonce,
    'x-signature': signature
  }
}

// Generic requester
async function request ({ method, path, params, data, accessKey, secretKey, headers = {}, responseType = 'json' }) {
  const envAccessKey = process.env.VUE_APP_HMAC_ACCESS_KEY || process.env.HMAC_ACCESS_KEY
  const envSecretKey = process.env.VUE_APP_HMAC_SECRET_KEY || process.env.HMAC_SECRET_KEY
  const finalAccessKey = accessKey || envAccessKey
  const finalSecretKey = secretKey || envSecretKey

  if (!finalAccessKey || !finalSecretKey) {
    throw new Error('HMAC access/secret key is missing. Set VUE_APP_HMAC_ACCESS_KEY and VUE_APP_HMAC_SECRET_KEY (or HMAC_ACCESS_KEY/HMAC_SECRET_KEY).')
  }
  const url = `${BASE_URL}${path}`
  // Build path with query exactly as requested by server (path + search)
  // If server expects a prefix in originalUrl (e.g., /api/public-secure), allow override via env
  const prefix = process.env.VUE_APP_API_PUBLIC_SECURE_PREFIX || ''
  const search = params && Object.keys(params || {}).length
    ? new URLSearchParams(params).toString()
    : ''
  const signedPath = `${prefix}${path}`
  const pathWithQuery = search ? `${signedPath}?${search}` : signedPath
  const hmacHeaders = buildHmacHeaders(
    { method, pathWithQuery, body: data },
    { secretKey: finalSecretKey }
  )

  // Set different headers based on response type
  const defaultHeaders = responseType === 'blob' 
    ? { Accept: '*/*' }
    : { Accept: 'application/json', 'Content-Type': 'application/json' }

  const config = {
    url,
    method,
    params,
    data,
    responseType,
    headers: {
      ...defaultHeaders,
      ...hmacHeaders,
      ...headers
    }
  }

  const res = await axios(config)
  return responseType === 'blob' ? res : res.data
}

export default {
  get: (path, { params, accessKey, secretKey, headers, responseType } = {}) =>
    request({ method: 'GET', path, params, accessKey, secretKey, headers, responseType }),
  post: (path, { data, accessKey, secretKey, headers, responseType } = {}) =>
    request({ method: 'POST', path, data, accessKey, secretKey, headers, responseType }),
  put: (path, { data, accessKey, secretKey, headers, responseType } = {}) =>
    request({ method: 'PUT', path, data, accessKey, secretKey, headers, responseType }),
  delete: (path, { params, data, accessKey, secretKey, headers, responseType } = {}) =>
    request({ method: 'DELETE', path, params, data, accessKey, secretKey, headers, responseType })
}
